import LogicalOperatorEqualsListView from "./LogicalOperatorEqualsListView";
import LogicalOperatoInputValue from "./LogicalOperatoInputValue";
import LogicalOperatorContainsView from "./LogicalOperatorContainsView";
import LogicalOperatorHasValueView from "./LogicalOperatorHasValueView";
import LogicalOperatorIsBetweenView from "./LogicalOperatorIsBetweenView";
import LogicalOperatorInTheLastView from "./LogicalOperatorInTheLastView";
import LogicalOperatorInBetweenDate from "./LogicalOperatorInBetweenDate";
import React from "react";
import LogicalOperatorBooleanView from "./LogicalOperatorBooleanView";

/**
 * Supported ordering by Bound filter type (Druid)
 */
export const BoundOrdering = {
  lexicographic: "lexicographic",
  alphanumeric: "alphanumeric",
  numeric: "numeric",
  strLen: "strlen",
  version: "version",
};

/**
 * Filter types supported by Druid
 */
export const LogicalOperatorTypes = {
  in: "in",
  not: "not",
  search: "search",
  regex: "regex",
  selector: "selector",
  bound: "bound",
  boolean: "boolean",
};

/**
 * What are the types supported by each logical operator
 */
export const SupportedTypes = {
  String: "String",
  Numeral: "Numeral",
  Boolean: "Boolean",
  Date: "Date",
};

/**
 * Maps what sort of options should be available for the primitive type
 * supported by the QB dimensions. All supported dimension types from QB:
 *  'string', 
    'text', 
    'boolean', 
    'long', 
    'double', 
    'date', 
    'timestamp', 
    null
 */
export const DimensionTypes = {
  string: ["String"],
  text: ["String"],
  boolean: ["String", "Boolean"],
  long: ["String", "Numeral"],
  double: ["String", "Numeral"],
  date: ["String", "Date"],
  timestamp: ["String", "Numeral"],
};

/**
 * Enumerator for the logical operator description name
 */
export const LogicalOperatorEnumerator = {
  Equals: "Equals",
  DoesNotEqual: "Does Not Equal",
  Contains: "Contains",
  DoesNotContain: "Does Not Contain",
  HasValue: "Has Value",
  DoesNotHaveValue: "Does Not Have Value",
  IsBetween: "Is Between",
  IsNotBetween: "Is Not Between",
  LessThan: "Less Than",
  GreaterThan: "Greater Than",
  InTheLast: "In The Last",
  BeforeTheLast: "Before The Last",
  InBetween: "In Between",
  NotInBetween: "Not In Between",
  On: "On",
  NotOn: "Not On",
  TrueFalse: "True/False",
  And: "and",
  Or: "or",
};

/**
 * Define each logical operator,
 * the type of data it should support and how should behave according it
 *     description: Name to be displayed in the Logical Operator selector
 *     displayedValue: Value to be displayed in the textbox between the values selected
 *     logicalType: What kind of type it should be applied in the Query Filter based on Druid
 *     supportedTypes: {
 *          supportedType: defines which primitive type will be supported by this logical operator. When we select "string" for example,
 *              the Logical Operator Selector will show all the logical operators that contains the same supportedType.
 *          view: view that should render for the primitive type currently selected.
 *     }
 */
export const LogicalOperatorsOptions = [
  {
    description: LogicalOperatorEnumerator.Equals,
    displayedValue: " = ",
    logicalType: LogicalOperatorTypes.in,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorEqualsListView,
      },
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatoInputValue,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.DoesNotEqual,
    displayedValue: " != ",
    logicalType: LogicalOperatorTypes.in,
    setObjection: true,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorEqualsListView,
      },
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatoInputValue,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.Contains,
    displayedValue: " contains ",
    logicalType: LogicalOperatorTypes.regex,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorContainsView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.DoesNotContain,
    displayedValue: " does not contain ",
    logicalType: LogicalOperatorTypes.regex,
    setObjection: true,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorContainsView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.HasValue,
    displayedValue: " has ",
    setObjection: true,
    logicalType: LogicalOperatorTypes.search,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorHasValueView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.DoesNotHaveValue,
    displayedValue: " doesn't have ",
    logicalType: LogicalOperatorTypes.search,
    supportedTypes: [
      {
        supportedType: SupportedTypes.String,
        view: LogicalOperatorHasValueView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.IsBetween,
    displayedValue: " is between ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatorIsBetweenView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.IsNotBetween,
    displayedValue: " isn't between ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: true,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatorIsBetweenView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.LessThan,
    displayedValue: " is less than ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatoInputValue,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.GreaterThan,
    displayedValue: " is greater than ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Numeral,
        view: LogicalOperatoInputValue,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.InTheLast,
    displayedValue: " in the last ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: LogicalOperatorInTheLastView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.BeforeTheLast,
    displayedValue: " before the last ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: LogicalOperatorInTheLastView,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.InBetween,
    displayedValue: " in between ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: LogicalOperatorInBetweenDate,
      },
    ],
  },
  {
    description: "Not In Between",
    displayedValue: " not in between ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: true,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: LogicalOperatorInBetweenDate,
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.On,
    displayedValue: " on ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: (props) => (
          <LogicalOperatorInBetweenDate {...props} singDatePicker />
        ),
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.NotOn,
    displayedValue: " not on ",
    logicalType: LogicalOperatorTypes.search,
    setObjection: true,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Date,
        view: (props) => (
          <LogicalOperatorInBetweenDate {...props} singDatePicker />
        ),
      },
    ],
  },
  {
    description: LogicalOperatorEnumerator.TrueFalse,
    displayedValue: " is ",
    logicalType: LogicalOperatorTypes.selector,
    setObjection: false,
    supportedTypes: [
      {
        supportedType: SupportedTypes.Boolean,
        view: LogicalOperatorBooleanView,
      },
    ],
  },
];

/**
 * Checks whether the current props is to be assigned to the current component for LO
 */
export const isPropsForLogicalOperator = ({
  choosed,
  logicalOperator,
  primitiveType,
}) =>
  // is there a LO set
  logicalOperator &&
  // does it already has data ?
  logicalOperator.value &&
  // is there a primitive type set to the coming data?
  logicalOperator.primitiveType &&
  logicalOperator.primitiveType === primitiveType &&
  // and is this data related to the choosed LO?
  (choosed.option === logicalOperator.description ||
    logicalOperator.primitiveType === primitiveType);
// if so.. return true which means that the data could be populated into the
// the state of the component
